import { useNavigate } from "react-router-dom";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const updated = "April 7, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 cursor-pointer">
          <div className="w-7 h-7 bg-[#16a34a] rounded-md flex items-center justify-center">
            <i className="ri-sun-line text-white text-xs"></i>
          </div>
          <span className="font-bold text-gray-900 text-base">SolarLead<span className="text-[#16a34a]">Source</span></span>
        </button>
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1 cursor-pointer">
          <i className="ri-arrow-left-line"></i> Back
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-400">Last updated: {updated}</p>
        </div>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p>SolarLeadSource (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the SolarLeadSource platform, which connects solar installation companies with homeowners interested in solar energy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.</p>
            <p className="mt-3">By using our platform, you agree to the collection and use of information in accordance with this policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. Information We Collect</h2>
            <h3 className="font-semibold text-gray-800 mb-2">2.1 Installer Account Information</h3>
            <p>When you register as an installer, we collect: company name, contact name, email address, phone number, service area (states and ZIP codes), and payment information (processed by Stripe — we do not store card details).</p>
            <h3 className="font-semibold text-gray-800 mb-2 mt-4">2.2 Lead Information</h3>
            <p>We collect homeowner information submitted through our lead generation forms, including name, address, phone number, email, monthly electricity bill, and property details. This information is only shared with installers who purchase the specific lead.</p>
            <h3 className="font-semibold text-gray-800 mb-2 mt-4">2.3 Usage Data</h3>
            <p>We automatically collect information about how you interact with our platform, including IP address, browser type, pages visited, and actions taken within the dashboard.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide and maintain the SolarLeadSource platform</li>
              <li>To process lead purchases and payments via Stripe</li>
              <li>To send transactional emails (purchase confirmations, account notifications)</li>
              <li>To match homeowner leads with qualified installers in the appropriate service area</li>
              <li>To improve our platform and user experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Data Sharing</h2>
            <p>We do not sell your personal information. We share data only in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Lead purchases:</strong> When an installer purchases a lead, the homeowner&apos;s contact details are shared exclusively with that installer.</li>
              <li><strong>Payment processing:</strong> Payment data is handled by Stripe. See Stripe&apos;s privacy policy at stripe.com/privacy.</li>
              <li><strong>Email delivery:</strong> We use Resend to send transactional emails.</li>
              <li><strong>Legal requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Data Security</h2>
            <p>We use Supabase for data storage with row-level security policies. All data is encrypted in transit using TLS. Payment information is processed by Stripe and never stored on our servers. We implement industry-standard security measures to protect your information.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Data Retention</h2>
            <p>We retain your account information for as long as your account is active. Lead data is retained for 3 years for record-keeping purposes. You may request deletion of your account and associated data by contacting us at privacy@solarleadsource.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of marketing communications</li>
              <li>Data portability (receive your data in a machine-readable format)</li>
            </ul>
            <p className="mt-3">To exercise these rights, contact us at privacy@solarleadsource.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">8. Cookies</h2>
            <p>We use essential cookies for authentication and session management. We do not use tracking or advertising cookies. You can control cookie settings through your browser.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">9. TCPA Compliance</h2>
            <p>All homeowner leads on our platform have provided express written consent to be contacted by solar installers. Installers are responsible for maintaining their own TCPA compliance when contacting leads.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">10. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, contact us at:</p>
            <div className="bg-gray-50 rounded-xl p-4 mt-3">
              <p className="font-semibold text-gray-900">SolarLeadSource</p>
              <p>Email: <a href="mailto:privacy@solarleadsource.com" className="text-[#16a34a] hover:underline">privacy@solarleadsource.com</a></p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
